import { loadStripe, Stripe } from '@stripe/stripe-js';
import { supabase } from './supabase';
import { toast } from './toast';
import { PRODUCTS } from '../stripe-config';
import { PRODUCTS } from '../stripe-config';

// Initialize Stripe with your publishable key
let stripePromise: Promise<Stripe | null>;

export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

export const createCheckoutSession = async (priceId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('You must be logged in to subscribe');
    }

    // Get user email
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('email')
      .eq('user_id', user.id)
      .single();

    if (profileError) {
      throw new Error('Failed to fetch user profile');
    }

    // Create a checkout session via Supabase Edge Function
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
      },
      body: JSON.stringify({
        priceId: priceId,
        userId: user.id,
        customerEmail: profile.email,
        success_url: `${window.location.origin}/account-type?success=true`,
        cancel_url: `${window.location.origin}/account-type?canceled=true`
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Checkout error response:', errorData);
      throw new Error(errorData.error || 'Failed to create checkout session. Please try again.');
    }

    const { sessionId, url } = await response.json();
    
    if (!url) {
      throw new Error('No checkout URL returned from server');
    }
    
    // Redirect to Stripe Checkout
    window.location.href = url;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to start checkout process');
  }
};

export const createPortalSession = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('You must be logged in to manage your subscription');
    }

    // Create a portal session via Supabase Edge Function
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
      },
      body: JSON.stringify({
        userId: user.id
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create portal session');
    }

    const { url } = await response.json();
    
    // Redirect to Stripe Customer Portal
    window.location.href = url;
  } catch (error) {
    console.error('Error creating portal session:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to access billing portal');
  }
};